import { format } from 'date-fns';
import { Clock, X } from 'lucide-react';

interface CalendarAgendaProps {
  days: any[];
  onDayClick: (day: any) => void;
  currentUserId?: string;
  isClickable: boolean;
  isParentOrAdmin?: boolean;
  onUnclaimSlot?: (slotId: string) => void;
  getUserColor: (user?: { color?: string | null; role?: string } | null, fallback?: string) => string;
  formatTime12Hour: (time: string) => string;
}

function computeRemainingAvailability(
  windows: { start_time: string; end_time: string }[],
  claimedSlots: { start_time: string; end_time: string; status: string }[]
): { start: string; end: string }[] {
  const activeSlots = claimedSlots.filter((s) => s.status !== 'cancelled');

  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };
  const minutesToTime = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const remaining: { start: string; end: string }[] = [];

  for (const window of windows) {
    const windowStart = timeToMinutes(window.start_time);
    const windowEnd = timeToMinutes(window.end_time);

    const overlappingSlots = activeSlots
      .filter((s) => {
        const slotStart = timeToMinutes(s.start_time);
        const slotEnd = timeToMinutes(s.end_time);
        return slotStart < windowEnd && slotEnd > windowStart;
      })
      .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

    let currentStart = windowStart;

    for (const slot of overlappingSlots) {
      const slotStart = Math.max(timeToMinutes(slot.start_time), windowStart);
      const slotEnd = Math.min(timeToMinutes(slot.end_time), windowEnd);

      if (slotStart > currentStart) {
        remaining.push({
          start: minutesToTime(currentStart),
          end: minutesToTime(slotStart),
        });
      }
      currentStart = Math.max(currentStart, slotEnd);
    }

    if (currentStart < windowEnd) {
      remaining.push({
        start: minutesToTime(currentStart),
        end: minutesToTime(windowEnd),
      });
    }
  }

  return remaining;
}

export function CalendarAgenda({ days, onDayClick, currentUserId, isClickable, isParentOrAdmin, onUnclaimSlot, getUserColor, formatTime12Hour }: CalendarAgendaProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Show all current-month days that have content (cutoff handled by CalendarTab)
  const relevantDays = days.filter((day) => {
    if (!day.isCurrentMonth) return false;
    return true;
  });

  if (relevantDays.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No upcoming availability or scheduled care this month.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {relevantDays.map((day) => {
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);
        const isDayPast = dayDate < today;
        const activeSlots = day.claimedSlots.filter((s: any) => s.status !== 'cancelled');
        // No green unclaimed windows for past days
        const remainingWindows = isDayPast ? [] : computeRemainingAvailability(day.availabilityWindows, day.claimedSlots);
        const hasContent = remainingWindows.length > 0 || activeSlots.length > 0;

        if (!hasContent && !day.isToday) return null;

        return (
          <button
            key={day.dateString}
            onClick={() => isClickable && !isDayPast && onDayClick(day)}
            disabled={!isClickable || isDayPast}
            data-testid={`clickable-agenda-day-${day.dateString.replace(/-/g, '')}`}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${
              isDayPast
                ? 'border-slate-700 bg-slate-800/50 opacity-60'
                : day.isToday
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-slate-700 bg-slate-800'
            } ${isClickable && !isDayPast ? 'hover:bg-slate-700 cursor-pointer' : 'cursor-default'}`}
          >
            {/* Day header */}
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium text-sm">
                  {format(day.date, 'EEE, MMM d')}
                </span>
                {day.isToday && (
                  <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded">
                    Today
                  </span>
                )}
              </div>
              {hasContent && (
                <span className="text-xs text-gray-500">
                  {activeSlots.length > 0 ? `${activeSlots.length} slot${activeSlots.length > 1 ? 's' : ''}` : 'Available'}
                </span>
              )}
            </div>

            {/* Remaining availability windows (after subtracting claimed slots) — not for past */}
            {remainingWindows.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {remainingWindows.map((w, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded"
                  >
                    <Clock size={10} />
                    {formatTime12Hour(w.start)}-{formatTime12Hour(w.end)}
                  </span>
                ))}
              </div>
            )}

            {/* Claimed slots */}
            {activeSlots.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {activeSlots.map((slot: any) => (
                  <span
                    key={slot.id}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: `${getUserColor(slot.caretaker)}20`,
                      color: getUserColor(slot.caretaker, '#9ca3af'),
                      borderLeft: slot.is_default ? `2px dashed ${getUserColor(slot.caretaker)}` : undefined,
                    }}
                  >
                    {slot.caretaker?.emoji && <span>{slot.caretaker.emoji}</span>}
                    {slot.caretaker?.first_name || 'Unknown'}
                    {slot.is_default && <span className="text-gray-500 text-[10px]">(default)</span>}
                    <span className="text-gray-500 ml-0.5">
                      {formatTime12Hour(slot.start_time)}-{formatTime12Hour(slot.end_time)}
                    </span>
                    {!isDayPast && !slot.is_default && onUnclaimSlot && (isParentOrAdmin || slot.caretaker_id === currentUserId) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUnclaimSlot(slot.id);
                        }}
                        className="text-red-400 hover:text-red-300 ml-0.5 flex-shrink-0"
                        title={isParentOrAdmin ? 'Delete this slot' : 'Unclaim this slot'}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* Empty today */}
            {!hasContent && day.isToday && (
              <p className="text-xs text-gray-500">No availability today</p>
            )}
          </button>
        );
      })}
    </div>
  );
}
